import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, tap } from 'rxjs/operators';
import { UserInfoService } from '@appStore/account/services/user-info.service';

import * as actions from '@appStore/account/actions';
import { ActionTypes } from '@appStore/account/actions';
import { SxCookieService } from 'app/services/sx-cookie.service';
import { UserType } from '@enums';
import { OidcSecurityService } from 'angular-auth-oidc-client'; //'@core/auth;

@Injectable()
export class UserInfoEffects {
  constructor(
    private router: Router,
    private userinfoService: UserInfoService,
    private sxCookieService: SxCookieService,
    private actions$: Actions,
    private oidcSecurityService: OidcSecurityService
  ) {}

  setLoginCookie$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.UserStartedLogin),
      map((action: actions.UserStartedLogin) => {
        this.sxCookieService.setCookie('SX_LOGGED_IN_AS', action.payload);
      })
    ),
    { dispatch: false }
  );

  removeLoginCookie$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.UserLoggingOut),
      map(() => {
        this.sxCookieService.clearCookie('SX_LOGGED_IN_AS');
     })
    ),
    { dispatch: false }
  );

  readLoginCookie$ = createEffect(() => {
    return   this.actions$.pipe(
      ofType(ActionTypes.UserInfoLoadItemSuccess),
      map((action: actions.UserInfoLoadSuccess) => {
        let userType = this.sxCookieService.getCookie('SX_LOGGED_IN_AS') as UserType;
        return new actions.UserInfoLoadComplete(userType);
      })
    );
  });
  
  updateAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.UserInfoUpdateItem),
      map((action: actions.UserInfoUpdate) => action.payload),
      exhaustMap(userinfo =>
        this.userinfoService.updateUserInfo(userinfo)
        .pipe(
          //tap(() => { this.oidcSecurityService.getUserinfo(false).subscribe(resp => {}); }),
          map(item => new actions.UserInfoUpdateSuccess(item)),
          catchError(error => of(new actions.UserInfoUpdateFail(error)))
        )
      )
    );
  });
  
  handleUserInfoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.UserInfoUpdateItemSuccess),
      map((action: actions.UserInfoUpdateSuccess) => action.payload),
      tap((userInfo) => {
        if (userInfo.onBoardingComplete && !userInfo.unValidatedEmail) {
          this.router.navigate([`/secure/dashboard`]);
        } else if (!userInfo.onBoardingComplete && userInfo.unValidatedEmail) {
          this.router.navigate([`/secure/register/verifyemail`]);
        } else if (userInfo.onBoardingComplete && userInfo.unValidatedEmail) {
          this.router.navigate([`/secure/register/verifyemailadditional`]);
        }
     })
    ),
    { dispatch: false }
  );

  inactivateAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.InactivateUser),
      map((action: actions.InactivateUser) => action.payload),
      exhaustMap(userinfo =>
        this.userinfoService.inactivateUserAccount(userinfo).pipe(
          //tap(() => { this.oidcSecurityService.getUserinfo(false).subscribe(resp => {});}),
          map(item => new actions.InactivateUserSuccess(item)),
          catchError(error => of(new actions.InactivateUserFail(error)))
        )
      )
    );
  });

  isUserRegisteredValid$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.IsUserRegistered),
      map((action: actions.IsUserRegistered) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsUserRegistered(item).pipe(
          map(validItem => new actions.IsUserRegisteredSuccess(validItem)),
          catchError(error => of(new actions.IsUserRegisteredFail(error)))
        )
      )
    );
  });
  
  isUserInfoOutDated$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.HasUserInfoExpired),
      exhaustMap(action =>
        this.userinfoService.getHasUserInfoExpired().pipe(
          map(validItem => new actions.HasUserInfoExpiredSuccess(validItem)),
          catchError(error => of(new actions.HasUserInfoExpiredFail(error)))
        )
      )
    );
  });

  isUserMarkedForInactivation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.IsUserMarkedForInactivation),
      map((action: actions.IsUserMarkedForInactivation) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsUserMarkedForInactivation(item).pipe(
          map(validItem => new actions.IsUserMarkedForInactivationSuccess(validItem)),
          catchError(error => of(new actions.IsUserMarkedForInactivationFail(error)))
        )
      )
    );
  });

  canUserBeInactivated$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.CanUserBeInactivated),
      exhaustMap(action =>
        this.userinfoService.getCanUserBeInactivated().pipe(
          map(validItem => new actions.CanUserBeInactivatedSuccess(validItem)),
          catchError(error => of(new actions.CanUserBeInactivatedFail(error)))
        )
      )
    );
  });
  
  updateUserInfoExpirationDate$ = createEffect(() => {
    return   this.actions$.pipe(
      ofType(ActionTypes.UserInfoSetUpdated),
      map((action: actions.UserInfoSetUpdated) => action.payload),
      exhaustMap(item =>
        this.userinfoService.updateUserInfoExpirationDate(item).pipe(
          //tap(() => {this.oidcSecurityService.getUserinfo(false).subscribe(resp => {});}),
          map(validItem => new actions.UserInfoSetUpdatedSuccess(validItem)),
          catchError(error => of(new actions.UserInfoSetUpdatedFail(error)))
        )
      )
    );
  });
  
  sendVerificationEmail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.SendVerificationEmail),
      tap((userInfo) =>{
        this.userinfoService.sendVerificationEmail().subscribe(resp =>{});
      }),
      map(validItem => new actions.SendVerificationEmailSuccess(validItem)),
      catchError(error => of(new actions.SendVerificationEmailFail(error)))
    );
  });

  setIntelligyLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.SetIntelligyLogin),
      tap(() =>{
        this.userinfoService.setIntelligyLogin().subscribe(success => {});
      }),
      map(()=> new actions.SetIntelligyLoginSuccess()),
      catchError(error => of(new actions.SetIntelligyLoginFail(error)))
    );
  });

  setStartupApplication$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActionTypes.SetStartupApplication),
      map((action: actions.SetStartupApplication) => action.payload),
      exhaustMap(startupApplication => this.userinfoService.setStartupApplication(startupApplication)
      .pipe(
        map(startupApplication => new actions.SetStartupApplicationSuccess(startupApplication)),
        catchError(error => of(new actions.SetStartupApplicationFail(error)))
        )
      )
    );
  });
}